<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { FilterIcon } from '@heroicons/vue/solid';

import { UiButton, UiMultiselect, UiRightPanel } from '@/components';
import type { AuraProcessingCostsFilters } from '@/types';

const props = defineProps<{
    modelValue: AuraProcessingCostsFilters;
    users: Array<{ id: number; name: string }>;
}>();
const emit = defineEmits<{ 'update:modelValue': [AuraProcessingCostsFilters] }>();
const { t } = useI18n();

const filtersOpened = ref(false);
const typeOptions = computed(() => [
    { label: t('aura.processingCosts.processingTypes.summary'), value: 'summary' },
    { label: t('aura.processingCosts.processingTypes.chunking'), value: 'chunking' },
    { label: t('aura.processingCosts.processingTypes.embedding'), value: 'embedding' },
    { label: t('aura.processingCosts.processingTypes.indexing'), value: 'indexing' },
    {
        label: t('aura.processingCosts.processingTypes.insight_extraction'),
        value: 'insight_extraction',
    },
    { label: t('aura.processingCosts.processingTypes.rag_engine'), value: 'rag_engine' },
    { label: t('aura.processingCosts.processingTypes.sql_engine'), value: 'sql_engine' },
    {
        label: t('aura.processingCosts.processingTypes.free_sql_agent'),
        value: 'free_sql_agent',
    },
    {
        label: t('aura.processingCosts.processingTypes.free_sql_narration'),
        value: 'free_sql_narration',
    },
    {
        label: t('aura.processingCosts.processingTypes.answer_generation'),
        value: 'answer_generation',
    },
    {
        label: t('aura.processingCosts.processingTypes.answer_agent_reasoning'),
        value: 'answer_agent_reasoning',
    },
    { label: t('aura.processingCosts.processingTypes.classify'), value: 'classify' },
    {
        label: t('aura.processingCosts.processingTypes.memory_summarize'),
        value: 'memory_summarize',
    },
    {
        label: t('aura.processingCosts.processingTypes.tenant_usage_limit'),
        value: 'tenant_usage_limit',
    },
    {
        label: t('aura.processingCosts.processingTypes.provider_rate_limit'),
        value: 'provider_rate_limit',
    },
    {
        label: t('aura.processingCosts.processingTypes.insight_catalog_selection'),
        value: 'insight_catalog_selection',
    },
    {
        label: t('aura.processingCosts.processingTypes.analytical_generation'),
        value: 'analytical_generation',
    },
    {
        label: t('aura.processingCosts.processingTypes.chain_loop_guard'),
        value: 'chain_loop_guard',
    },
    {
        label: t('aura.processingCosts.processingTypes.chain_budget_guard'),
        value: 'chain_budget_guard',
    },
    { label: t('aura.processingCosts.processingTypes.llm_invoke'), value: 'llm_invoke' },
]);
const statusOptions = computed(() => [
    { label: t('aura.processingCosts.statuses.completed'), value: 'completed' },
    { label: t('aura.processingCosts.statuses.failed'), value: 'failed' },
    { label: t('aura.processingCosts.statuses.skipped'), value: 'skipped' },
    { label: t('aura.processingCosts.statuses.pending'), value: 'pending' },
    { label: t('aura.processingCosts.statuses.processing'), value: 'processing' },
]);

const update = (changes: AuraProcessingCostsFilters) =>
    emit('update:modelValue', { ...props.modelValue, ...changes, page: 1 });

const clearFilters = () =>
    update({ userId: undefined, processingType: undefined, status: undefined });
</script>

<template>
    <UiRightPanel v-model="filtersOpened" :title="$t('filter.sidebar.title')">
        <template #button>
            <UiButton
                size="md"
                variant="white"
                text-variant="gray-700"
                @click="filtersOpened = true"
            >
                <FilterIcon class="mr-0 h-5 w-5 text-gray-400 sm:mr-3" aria-hidden="true" />
                <span class="hidden sm:block">{{ $t('core.actions.Filter') }}</span>
            </UiButton>
        </template>
        <template #default>
            <form autocomplete="off" class="flex flex-col gap-4">
                <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-800">
                        {{ $t('aura.processingCosts.filters.userLabel') }}
                    </label>
                    <UiMultiselect
                        name="processing_costs_user"
                        :model-value="modelValue.userId"
                        :options="users"
                        label="name"
                        value-prop="id"
                        primitive
                        can-clear
                        :placeholder="$t('core.options.All')"
                        @update:model-value="update({ userId: $event || undefined })"
                    />
                </div>
                <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-800">
                        {{ $t('aura.processingCosts.filters.typeLabel') }}
                    </label>
                    <UiMultiselect
                        name="processing_costs_type"
                        :model-value="modelValue.processingType"
                        :options="typeOptions"
                        label="label"
                        value-prop="value"
                        primitive
                        can-clear
                        :placeholder="$t('core.options.All')"
                        @update:model-value="update({ processingType: $event || undefined })"
                    />
                </div>
                <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-800">
                        {{ $t('aura.processingCosts.filters.statusLabel') }}
                    </label>
                    <UiMultiselect
                        name="processing_costs_status"
                        :model-value="modelValue.status"
                        :options="statusOptions"
                        label="label"
                        value-prop="value"
                        primitive
                        can-clear
                        :placeholder="$t('core.options.All')"
                        @update:model-value="update({ status: $event || undefined })"
                    />
                </div>
                <div class="mt-4 grid grid-cols-2 gap-4">
                    <UiButton variant="white" text-variant="gray-700" @click="clearFilters">
                        {{ $t('core.actions.Clear') }}
                    </UiButton>
                    <UiButton variant="primary" text-variant="white" @click="filtersOpened = false">
                        {{ $t('core.actions.Apply') }}
                    </UiButton>
                </div>
            </form>
        </template>
    </UiRightPanel>
</template>
